import { NgClass, NgStyle, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { SelectionModel } from '@angular/cdk/collections';
import { Router, ActivatedRoute, Params, RouterLink } from '@angular/router';
import { Component, Input, OnInit, ViewChild, OnDestroy, Output, EventEmitter, AfterContentInit, ContentChildren, QueryList, TemplateRef, ContentChild, inject, ChangeDetectionStrategy, TrackByFunction } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatColumnDef, MatHeaderRowDef, MatRowDef, MatTable, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { L10nLocale, L10N_LOCALE, L10nTranslationService, L10nTranslatePipe } from 'angular-l10n';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Observable, Subscription, pairwise } from 'rxjs';
import { BaseDataSource, FancyRowClass, TableColumn } from 'src/app/shared/data-table/datasource/datasource';
import { CustomizeColumnsDialogComponent } from './customize-columns-dialog/customize-columns-dialog.component';
import { BalsaCardComponent } from '../balsa-card/balsa-card.component';
import { ErrorMessageComponent } from '../error-message/error-message.component';
import { TenantSearchComponent } from '../tenant-search/tenant-search.component';
import { AuthService } from '../authentication/auth.service';
import { Tenant } from 'src/app/hickory-api';
import { LogHelper } from '../logging/log-helper';

type HasId = {
  id?: number | string;
};

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: true,
  imports: [
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    AsyncPipe,
    RouterLink,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltipModule,
    BalsaCardComponent,
    TenantSearchComponent,
    ErrorMessageComponent,
    L10nTranslatePipe,
  ],
})
export class DataTableComponent<TModel extends object, TService, TRequest>
  implements OnInit, OnDestroy, AfterContentInit
{
  readonly locale = inject<L10nLocale>(L10N_LOCALE);
  readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly translation = inject(L10nTranslationService);

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatTable, { static: true }) table: MatTable<TModel>;
  @ContentChild('expandedRowTemplate', { static: true }) expandedRow: TemplateRef<unknown>;

  @ContentChildren(MatHeaderRowDef) headerRowDefs: QueryList<MatHeaderRowDef>;
  @ContentChildren(MatRowDef) rowDefs: QueryList<MatRowDef<TModel>>;
  @ContentChildren(MatColumnDef) columnDefs: QueryList<MatColumnDef>;

  @Input() title: string;
  @Input() subtitle: string;
  @Input() titleIcon: string;
  @Input() cardMargin = '1rem';
  @Input() headerMargin = '0';
  @Input() defaultPageSize = 10;
  @Input() dataSource: BaseDataSource<TModel, TService, TRequest>;
  @Input() selectionModel: SelectionModel<TModel & HasId>;
  @Input() additionalColumns: string[] = [];
  @Input() useDefaultSearch = true;
  @Input() searchLabel = 'Contacts.List.Search';
  @Input() fullWidthSearch = false;
  @Input() useSelection = false;
  @Input() singleSelection = false;
  @Input() canRowExpand = false;
  @Input() canChangeColumns = true;
  @Input() updateRoute = true;
  @Input() hasDivider = true;
  @Input() centered = true;
  @Input() useCalculatedMaxHeight = false;
  @Input() additionalHeightOffset = '0px';
  @Input() paginatorMinimum = this.defaultPageSize;
  @Input() stickySelect = false;
  @Input() showTenantSearch = false;
  @Input() cardShadow = true;
  @Input() disabled = false;
  @Input() tableId: string; // Unique identifier for storing column preferences
  @Input() useMarginBottom = true;
  @Input() hidePaginator = false;
  @Input() rowClassModifiers: Record<FancyRowClass, (row: TModel) => boolean> = null;
  @Input() fixedTableLayout = false;
  @Input() rowIdentity: (row: TModel) => any = (row) => row; // Unique identifier for the row - by default, the row itself is the identifier

  @Output() rowClicked = new EventEmitter<TModel>();
  @Output() selectionChange = new EventEmitter<(TModel & HasId)[]>();
  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() sortChange = new EventEmitter<Sort>();

  compareFn = (entity1: TModel & HasId, entity2: TModel & HasId) => {
    return entity1?.id === entity2?.id;
  };
  selection = new SelectionModel<TModel & HasId>(true, null, true, this.compareFn);

  trackByFn: TrackByFunction<TModel> = (_, item) => this.rowIdentity(item);

  subscription = new Subscription();

  displayedColumns: string[] = [];
  expandedElement: TModel | null;

  @Input() availablePageSizes = [10, 25, 50, 100, 200];

  get availableColumns() {
    return this.dataSource?.availableColumns;
  }

  get displayedAndAdditionalCols() {
    const columns = [];

    if (this.useSelection) {
      columns.push('select');
    }

    return columns.concat(this.displayedColumns, this.additionalColumns);
  }

  get error$(): Observable<string> {
    return this.dataSource?.error$;
  }

  get isSuperAdmin(): boolean {
    return this.authService.isSuperAdmin();
  }

  get rowIsClickable(): boolean {
    return this.rowClicked.observed;
  }

  selectedTenantId: string;
  filteredTenantIdMessage: string;

  private readonly STORAGE_KEY = 'data-table-columns';

  // after the <ng-content> has been initialized, the column definitions are available.
  // All that's left is to add them to the table ourselves:
  // https://stackoverflow.com/questions/59088912/angular-ng-content-within-mat-table
  ngAfterContentInit() {
    this.columnDefs.forEach((columnDef) => this.table.addColumnDef(columnDef));
    this.rowDefs.forEach((rowDef) => this.table.addRowDef(rowDef));
    this.headerRowDefs.forEach((headerRowDef) => this.table.addHeaderRowDef(headerRowDef));
  }

  ngOnInit(): void {
    this.checkQueryParams();

    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    if (this.updateRoute) {
      this.subscription.add(
        this.dataSource?.currentRequest$.pipe(pairwise()).subscribe(([previous, _]) => {
          if (!previous) {
            return;
          }

          void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: this.dataSource?.queryArguments,
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        }),
      );
    }

    this.subscription.add(
      this.dataSource?.columnsChanged$.subscribe(() => {
        const defaultColumns = this.dataSource?.availableColumns
          .filter((column) => !column.hideByDefault)
          .map((column) => column.field);

        // Use default columns if no tableId or column customization disabled
        if (!this.tableId || !this.canChangeColumns) {
          this.displayedColumns = [...defaultColumns];
          return;
        }

        // Try to load saved preferences
        const savedColumns = this.loadColumnPreferences();
        if (!savedColumns) {
          this.displayedColumns = [...defaultColumns];
          return;
        }

        // Validate saved columns still exist
        const availableColumnFields = new Set(this.dataSource?.availableColumns.map((col) => col.field));
        const validSavedColumns = savedColumns.filter((col) => availableColumnFields.has(col));

        // Use saved columns if valid, otherwise fall back to defaults
        this.displayedColumns = validSavedColumns.length > 0 ? [...validSavedColumns] : [...defaultColumns];
      }),
    );

    if (this.canRowExpand) {
      this.additionalColumns = [...this.additionalColumns, 'expand'];
    }

    if (this.selectionModel) {
      this.selection = this.selectionModel;
    }

    this.subscription.add(
      this.selection.changed.subscribe((_) => {
        this.selectionChange.emit(this.selection.selected);
      }),
    );

    this.dataSource?.init();
  }

  ngOnDestroy(): void {
    this.dataSource?.unsubscribe();
    this.subscription.unsubscribe();
  }

  checkQueryParams(): void {
    this.subscription.add(this.route.queryParams.subscribe(this.populatePageSizeAndTenantData.bind(this)));
  }

  populatePageSizeAndTenantData(queryParams: Params): void {
    const page: number = queryParams.page ?? 1;
    const sort: string = queryParams.sort ?? undefined;

    this.defaultPageSize = queryParams.pageSize ?? this.defaultPageSize;

    if (!!queryParams.pageSize && !this.availablePageSizes.some((x) => x == queryParams.pageSize)) {
      this.defaultPageSize = this.availablePageSizes[0];
    }

    if (this.paginator) {
      this.paginator.pageIndex = page - 1;
      this.paginator.pageSize = this.defaultPageSize;
    }

    if (this.showTenantSearch) {
      const user = this.authService.user.snapshot;
      this.selectedTenantId = queryParams.tenantId || user?.tenantId.toString();
      this.dataSource?.setNextRequest({ tenantId: this.selectedTenantId } as TRequest);
    }

    this.checkSortParam(sort);
    if (this.dataSource?.isReady) {
      this.onReload(false);
    }
  }

  checkSortParam(querySort: string): void {
    let sortDirection: SortDirection = 'asc';
    let sort = '';

    if (querySort?.at(0) === '-') {
      sortDirection = 'desc';
      sort = querySort?.substring(1);
    } else {
      sort = querySort;
    }

    if (sort && this.sort) {
      this.sort.active = sort;
      this.sort.direction = sortDirection;
    }
  }

  onReload(forceRefresh: boolean): void {
    this.dataSource?.loadData(forceRefresh);
  }

  // Custom selection logic to handle compareWith because of some bug
  // the SelectionModel does not seem to work always as expected:
  // https://github.com/angular/components/issues/25878
  // https://stackblitz.com/edit/components-issue-rkekpt?file=src%2Fapp%2Fexample-component.ts
  onToggleItem(model: TModel & HasId): void {
    if (!this.useSelection) {
      return;
    }

    if (this.singleSelection && this.selection.isSelected(model)) {
      this.selection.clear();
      return;
    }

    if (this.singleSelection && !this.selection.isSelected(model)) {
      this.selection.clear();
      this.selection.toggle(model);
      return;
    }

    if (this.selection.compareWith && this.selection.isSelected(model)) {
      const matchingRow = this.selection.selected.find((selectedRow) => this.selection.compareWith(selectedRow, model));

      if (matchingRow) {
        this.selection.toggle(matchingRow);
      }
    } else {
      this.selection.toggle(model);
    }
  }

  onToggleAll(): void {
    if (this.singleSelection && this.selection.selected.length) {
      this.selection.clear();
      return;
    }

    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.select(...(this.dataSource?.data ?? []));
    }
  }

  isAllSelected(): boolean {
    return (
      this.dataSource?.data?.length > 0 &&
      this.dataSource?.data?.every((v: TModel & HasId) => this.selection.isSelected(v))
    );
  }

  isSomeSelected(): boolean {
    const selected = this.dataSource?.data?.filter((v: TModel & HasId) => this.selection.isSelected(v)).length;

    return (
      (selected > 0 && selected < this.dataSource?.data?.length) ||
      (this.selection.selected.length > 0 && !this.isAllSelected())
    );
  }

  getValue(value: TableColumn<TModel>, element: TModel) {
    if (value.render) {
      return value.render(element) ?? '';
    }

    if (value.field.includes('.') && !element?.[value.field]) {
      const fields = value.field.split('.');
      let currentElement = element;
      for (const field of fields) {
        currentElement = currentElement?.[field];
      }
      return currentElement ?? '';
    }

    return element?.[value.field] ?? '';
  }

  onRowClicked(row: TModel) {
    const isExpanded = this.expandedElement !== null && this.rowIdentity(this.expandedElement) === this.rowIdentity(row);

    this.expandedElement = isExpanded ? null : row;
    if (!this.disabled) {
      this.rowClicked.emit(row);
      this.onToggleItem(row);
    }
  }

  onKeyUp(target: EventTarget) {
    this.dataSource?.searchTerm$.next((target as HTMLInputElement).value);
  }

  onOpenColumns() {
    const dialog = this.dialog.open(CustomizeColumnsDialogComponent, {
      width: '500px',
      data: {
        availableColumns: this.dataSource?.availableColumns,
        displayedColumns: this.displayedColumns,
      },
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
        this.displayedColumns = result;

        if (this.tableId && this.canChangeColumns) {
          this.saveColumnPreferences(result);
        }
      }
    });
  }

  onTenantSelected(tenant: Tenant) {
    if (!tenant) {
      tenant = {
        id: this.authService.user.snapshot.tenantId,
        name: this.authService.user.snapshot.agency,
      };
    }
    const tenantId = tenant.id.toString();
    this.selectedTenantId = tenantId;
    this.setFilteredTenantMessage(tenant);
    this.dataSource?.setNextRequest({ tenantId } as TRequest);
  }

  setFilteredTenantMessage(tenant: Tenant) {
    const title = this.translation.translate(this.title, null, this.locale.language);
    const tenantInfo = `(${tenant.id}) ${tenant.name}`;
    this.filteredTenantIdMessage = this.translation.translate(
      'DataTable.FilterByTenantMessage',
      { dataType: title, tenant: tenantInfo },
      this.locale.language,
    );
  }

  private saveColumnPreferences(displayedColumns: string[]): void {
    if (!this.tableId) {
      return;
    }

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      const allPreferences = stored ? (JSON.parse(stored) as Record<string, { displayedColumns: string[] }>) : {};

      allPreferences[this.tableId] = { displayedColumns };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allPreferences));
    } catch (error) {
      LogHelper.debug('Failed to save column preferences to localStorage:', error);
    }
  }

  private loadColumnPreferences(): string[] | null {
    if (!this.tableId) {
      return null;
    }

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);

      if (!stored) {
        return null;
      }

      const allPreferences = JSON.parse(stored) as Record<string, { displayedColumns?: string[] }>;
      const tablePreferences = allPreferences[this.tableId];

      return tablePreferences?.displayedColumns || null;
    } catch (error) {
      LogHelper.debug('Failed to load column preferences from localStorage:', error);
      return null;
    }
  }

  clearColumnPreferences(allTables = false): void {
    try {
      if (allTables) {
        localStorage.removeItem(this.STORAGE_KEY);
      } else if (this.tableId) {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
          const allPreferences = JSON.parse(stored) as Record<string, { displayedColumns: string[] }>;
          delete allPreferences[this.tableId];

          if (Object.keys(allPreferences).length === 0) {
            localStorage.removeItem(this.STORAGE_KEY);
          } else {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allPreferences));
          }
        }
      }
    } catch (error) {
      LogHelper.debug('Failed to clear column preferences from localStorage:', error);
    }
  }

  protected getColumnId(col: TableColumn<object>): string {
    return `column-${col.field}-${col.localizationKey}`;
  }

  protected getRowClass(row: TModel): string {
    if (!this.rowClassModifiers) {
      return '';
    }
    return Object.entries(this.rowClassModifiers)
      .map(([className, predicate]) => {
        return predicate(row) ? className : '';
      })
      .join(' ');
  }
}
